import { useEffect, useRef, useState } from "react";

import { SyncLoader } from "react-spinners";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { useCustomerContext } from "../../../../Context/CustomerContext";
import { type AddPermissionsEventType, type Insight, InsightProviderId } from "../../types";
import { calculatePermissionsMetrics } from "../../utils";
import Coverage from "./Coverage";
import PermissionsNeeded, { type PermissionsNeededAccounts, type PermissionsNeededProvider } from "./PermissionsNeeded";

type Props = {
  totalInsightsNum: number;
  permissionsNeededInsights: Insight[];
  permissionsNeededAccounts: PermissionsNeededAccounts;
  hasGKEAccess: boolean;
  hasMissingGKEPermissions: boolean;
  hasMissingCloudConnectPermissions: boolean;
  nonHealthyOrganizationCloudConnects: string[];
  tempSecurityHubInsightsNum?: number;
  errorLoadingAccounts: Error | null;
  isLoading: boolean;
  open: boolean;
  onClose: () => void;
  onAddPermissionsClicked: (eventType: AddPermissionsEventType) => void;
};

const PermissionsSummaryModal: React.FC<Props> = ({
  totalInsightsNum,
  permissionsNeededInsights,
  permissionsNeededAccounts,
  hasGKEAccess,
  hasMissingGKEPermissions,
  hasMissingCloudConnectPermissions,
  nonHealthyOrganizationCloudConnects,
  tempSecurityHubInsightsNum = 0,
  errorLoadingAccounts,
  isLoading,
  open,
  onClose,
  onAddPermissionsClicked,
}) => {
  const theme = useTheme();

  const { customer, hasGCPAssets } = useCustomerContext();

  const calculationPerformedRef = useRef(false);

  // Temporary solution for compatibility with securityHub insights
  // TODO: Remove totalInsightsNumWuthSecurityHub when we start receiving security-hub insights
  const totalInsightsNumWuthSecurityHub = totalInsightsNum + tempSecurityHubInsightsNum;
  const permissionsNeededInsightsWithSecurityHub = permissionsNeededInsights.length + tempSecurityHubInsightsNum;

  const [permissionsNeededProviders, setPermissionsNeededProviders] = useState<PermissionsNeededProvider[]>([]);

  const { permissionsCoverage, grantedPermissionsInsights: grantedPermissionsInsightsNum } =
    calculatePermissionsMetrics(totalInsightsNumWuthSecurityHub, permissionsNeededInsightsWithSecurityHub);

  const trustedAdvisorAccounts = permissionsNeededAccounts[InsightProviderId.TRUSTED_ADVISOR];
  const costOptimizationHubAccounts = permissionsNeededAccounts[InsightProviderId.COST_OPTIMIZATION_HUB];
  const securityHubAccounts = permissionsNeededAccounts[InsightProviderId.SECURITY_HUB];

  // Handle calculation only once, after accounts are loaded successfully
  useEffect(() => {
    if (open && !isLoading && !errorLoadingAccounts && !calculationPerformedRef.current) {
      const providers: PermissionsNeededProvider[] = [];

      if (trustedAdvisorAccounts.length) {
        const permissionsNeededNum = permissionsNeededInsights.filter(
          (insight) => insight.providerId === InsightProviderId.TRUSTED_ADVISOR
        ).length;
        if (permissionsNeededNum > 0) {
          providers.push({ id: InsightProviderId.TRUSTED_ADVISOR, insightsNum: permissionsNeededNum });
        }
      }

      if (costOptimizationHubAccounts.length) {
        const permissionsNeededNum = permissionsNeededInsights.filter(
          (insight) => insight.providerId === InsightProviderId.COST_OPTIMIZATION_HUB
        ).length;
        if (permissionsNeededNum > 0) {
          providers.push({
            id: InsightProviderId.COST_OPTIMIZATION_HUB,
            insightsNum: permissionsNeededNum,
          });
        }
      }

      // TODO: Remove tempSecurityHubInsightsNum condition when we start receiving security-hub insights
      if (tempSecurityHubInsightsNum) {
        providers.push({
          id: InsightProviderId.SECURITY_HUB,
          insightsNum: tempSecurityHubInsightsNum,
        });
      } else if (securityHubAccounts.length) {
        const permissionsNeededNum = permissionsNeededInsights.filter(
          (insight) => insight.providerId === InsightProviderId.SECURITY_HUB
        ).length;
        if (permissionsNeededNum > 0) {
          providers.push({
            id: InsightProviderId.SECURITY_HUB,
            insightsNum: permissionsNeededNum,
          });
        }
      }

      if (hasGCPAssets) {
        const gcpPermissionsNeededNum = permissionsNeededInsights.filter(
          (insight) => insight.providerId === InsightProviderId.GCP_RECOMMENDER
        ).length;
        if (gcpPermissionsNeededNum > 0) {
          providers.push({
            id: InsightProviderId.GCP_RECOMMENDER,
            insightsNum: gcpPermissionsNeededNum,
          });
        }
      }

      if (hasGKEAccess && hasMissingGKEPermissions) {
        const gkePermissionsNeededNum = permissionsNeededInsights.filter(
          (insight) => insight.providerId === InsightProviderId.FSK8S && insight.cloudTags.includes("gcp")
        ).length;
        if (gkePermissionsNeededNum > 0) {
          providers.push({
            id: InsightProviderId.FSK8S,
            insightsNum: gkePermissionsNeededNum,
          });
        }
      }

      setPermissionsNeededProviders(providers);
      calculationPerformedRef.current = true;
    }
  }, [
    open,
    isLoading,
    errorLoadingAccounts,
    trustedAdvisorAccounts,
    costOptimizationHubAccounts,
    securityHubAccounts,
    permissionsNeededInsights,
    hasMissingGKEPermissions,
    hasGCPAssets,
    hasGKEAccess,
    tempSecurityHubInsightsNum,
  ]);

  return (
    <Dialog
      data-cy="coverage-modal"
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: {
          style: {
            maxWidth: "900px",
          },
        },
      }}
    >
      <DialogTitle>Insights coverage</DialogTitle>

      <DialogContent dividers>
        <Box
          mt={1}
          gap={3}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <Box sx={{ flex: 2 }}>
            <Typography fontWeight={500}>About</Typography>
            {permissionsNeededInsightsWithSecurityHub > 0 ? (
              <Typography variant="body1" mt={1} sx={{ pr: { xs: 0, md: 6 } }}>
                DoiT currently assesses potential impacts for {permissionsCoverage}% of a total of {totalInsightsNum}{" "}
                insights.
                <br /> <br />
                Grant broader permissions to enable the analysis of {permissionsNeededInsightsWithSecurityHub}{" "}
                additional insights and reveal potential impacts you're currently missing. Follow the instructions below
                to increase your coverage.
              </Typography>
            ) : (
              <Typography variant="body1" mt={1} sx={{ pr: { xs: 0, md: 6 } }}>
                You have 100% insights coverage. DoiT is assessing potential impacts for all {totalInsightsNum}{" "}
                insights.
                <br /> <br />
                No further action is required.
              </Typography>
            )}
          </Box>

          <Coverage
            coverage={permissionsCoverage}
            totalInsightsNum={totalInsightsNum}
            grantedPermissionsInsightsNum={grantedPermissionsInsightsNum}
            permissionsNeededInsightsNum={permissionsNeededInsightsWithSecurityHub}
          />
        </Box>
        {permissionsNeededInsightsWithSecurityHub > 0 && (
          <Box mt={3}>
            {isLoading && (
              <Box
                mt={5}
                mb={5}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <SyncLoader size={10} color={theme.palette.primary.main} loading />
              </Box>
            )}
            {errorLoadingAccounts && (
              <Box
                mt={6}
                mb={6}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1" color="error">
                  Something went wrong. Please try again later.
                </Typography>
              </Box>
            )}
            {!isLoading && !errorLoadingAccounts && (
              <PermissionsNeeded
                permissionsNeededProviders={permissionsNeededProviders}
                customerId={customer.id}
                hasMissingCloudConnectPermissions={hasMissingCloudConnectPermissions}
                permissionsNeededAccounts={permissionsNeededAccounts}
                nonHealthyOrganizationCloudConnects={nonHealthyOrganizationCloudConnects}
                onAddPermissionsClicked={onAddPermissionsClicked}
              />
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 1, py: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ px: 2, py: 1 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PermissionsSummaryModal;
